/* eslint-disable camelcase */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useCompany from 'hooks/api/useCompany';
import useExpectedPayments, { expectedPaymentsAllDataType } from 'hooks/api/useExpectedPayments';
import useErrorCallback from 'hooks/useErrorCallback';
import { RefreshContext } from 'contexts/RefreshContext';
import { tableHeaderStyles } from 'theme/styles';
import useRouteTitle from 'hooks/useRouteTitle';
import { TargetsType } from 'types/CompanyTypes';
import { ExpectedPaymentsFilters } from 'consts/tableFilters';
import setUrlStringParams from 'helpers/setUrlStringParams';
import { expectedPaymentsParamsType, expectedPaymentsType } from 'queries/expectedPayments/getExpectedPayments';
import { removeEmptyValuesFromObject } from 'helpers/dt';
import Select from 'components/uiKit/Select';
import CompanySelect from 'components/CompanySelect';

import styles from './styles.module.scss';

interface useExpectedPaymentsType {
  expectedPaymentsData: expectedPaymentsAllDataType;
  getExpectedPayments: (data: expectedPaymentsType) => void;
  expectedPaymentsTableParams: expectedPaymentsParamsType;
  setExpectedPaymentsTableParams: () => void;
  setExpectedPaymentsRequestError: (value: boolean) => void;
  listTarget: TargetsType[];
  latePayments: TargetsType[];
}

const aoTypeOptions = [
  { key: 'classic', value: 'Classic (MAX)' },
  { key: 'available_receivables', value: 'Now' },
  { key: 'future', value: 'Future' },
];

const normalizeAoType = (aoType?: string) => {
  if (aoType === 'future_receivables') {
    return 'future';
  }

  return aoType || '';
};

const ExpectedPaymentsHeader: FC | any = ({ requestParams, refreshCount }) => {
  const { t } = useTranslation();
  const classes = tableHeaderStyles();
  const { title } = useRouteTitle();
  const {
    companiesData: { companiesData = [] },
    getCompanies,
  } = useCompany();
  const {
    listTarget,
    latePayments,
    getExpectedPayments,
    expectedPaymentsTableParams,
    setExpectedPaymentsTableParams,
    setExpectedPaymentsRequestError,
  }: useExpectedPaymentsType = useExpectedPayments();
  const { errorCallback } = useErrorCallback();
  const refreshContext = useContext(RefreshContext);
  const { company_id } = expectedPaymentsTableParams;
  const [validate, setValidate] = useState<boolean>(false);
  const [type, setType] = useState<TargetsType>(null);
  const [showLatePayments, setShowLatePayments] = useState<TargetsType>(null);
  const [selectedAoType, setSelectedAoType] = useState<string>('');
  const requestExpectedPayments = useCallback(
    (params: expectedPaymentsParamsType) => {
      setExpectedPaymentsRequestError(false);
      getExpectedPayments({
        params: removeEmptyValuesFromObject<expectedPaymentsParamsType>(params),
        successCallback: () => setExpectedPaymentsRequestError(false),
        errorCallback: (...args) => {
          setExpectedPaymentsRequestError(true);
          errorCallback(...args);
        },
      });
    },
    [getExpectedPayments, errorCallback, setExpectedPaymentsRequestError],
  );

  useEffect(() => {
    getCompanies({ errorCallback });
  }, [getCompanies, errorCallback]);

  useEffect(() => {
    const newParams = refreshContext.getNewParams({
      datakey: 'expectedPaymentsTableParams',
      params: expectedPaymentsTableParams,
      requestParams,
    });
    const normalizedParams = {
      ...newParams,
      ao_type: normalizeAoType(String(newParams.ao_type || '')),
    };
    setExpectedPaymentsTableParams(normalizedParams);

    if (normalizedParams.payment_type) {
      const target = listTarget.find((item) => item.key === normalizedParams.payment_type);

      if (target) {
        setType(target);
      }
    }

    if (normalizedParams.dont_show_late_payments) {
      const target = latePayments.find((item) => item.key === normalizedParams.dont_show_late_payments);

      if (target) {
        setShowLatePayments(target);
      }
    }

    if (normalizedParams.ao_type) {
      setSelectedAoType(String(normalizedParams.ao_type));
    }

    setValidate(true);
    requestExpectedPayments(normalizedParams);
  }, [refreshCount, requestExpectedPayments]);

  useEffect(() => {
    refreshContext.setParams({ datakey: 'expectedPaymentsTableParams', params: expectedPaymentsTableParams });
    setUrlStringParams({ filters: ExpectedPaymentsFilters, currentScreenParams: expectedPaymentsTableParams });
  }, [expectedPaymentsTableParams]);

  const handleCompanyChange = useCallback(
    (_, value) => {
      const source = value?.source;
      const newParams = {
        ...expectedPaymentsTableParams,
        company_id: source ? source.id : '',
      };

      if (validate) {
        requestExpectedPayments(newParams);
      }
      setExpectedPaymentsTableParams(newParams);
    },
    [setExpectedPaymentsTableParams, expectedPaymentsTableParams, validate, requestExpectedPayments],
  );

  const handleTypeChange = useCallback(
    (value: string) => {
      const newType = listTarget.find((e) => e.key === value);
      const newParams = {
        ...expectedPaymentsTableParams,
        payment_type: newType ? newType.key : '',
      };

      if (validate) {
        setType(newType ? { name: newType.name, key: newType.key } : null);
        requestExpectedPayments(newParams);
      }
      setExpectedPaymentsTableParams(newParams);
    },
    [expectedPaymentsTableParams, listTarget, validate, requestExpectedPayments],
  );

  const handleLatePaymentsChange = useCallback(
    (value: string) => {
      const newLatePayments = latePayments.find((e) => e.key === value);
      const newParams = {
        ...expectedPaymentsTableParams,
        dont_show_late_payments: newLatePayments ? newLatePayments.key : '',
      };

      if (validate) {
        setShowLatePayments(newLatePayments ? { name: newLatePayments.name, key: newLatePayments.key } : null);
        requestExpectedPayments(newParams);
      }
      setExpectedPaymentsTableParams(newParams);
    },
    [expectedPaymentsTableParams, latePayments, validate, requestExpectedPayments],
  );

  const handleAoTypeChange = useCallback(
    (value: string) => {
      const aoType = normalizeAoType(value);
      setSelectedAoType(aoType);
      const newParams = {
        ...expectedPaymentsTableParams,
        ao_type: aoType,
      };

      if (validate) {
        requestExpectedPayments(newParams);
      }
      setExpectedPaymentsTableParams(newParams);
    },
    [expectedPaymentsTableParams, validate, requestExpectedPayments, setExpectedPaymentsTableParams],
  );

  return (
    <div className={styles.root}>
      <div className={styles.title}>{title}</div>
      <div className={styles.container}>
        <form className={classes.form} noValidate autoComplete="off">
          <div className={styles.company}>
            <CompanySelect
              label={t('contentHeader.company')}
              value={company_id}
              onChange={handleCompanyChange}
              allowClear
            />
          </div>
          <div className={styles.type}>
            <Select
              label={t('tables.expectedPaymentsTable.type')}
              options={listTarget
                .filter((item) => item.key !== 'bank_fee')
                .map((e) => ({ key: e.key, value: t(e.name) }))}
              value={type === null ? '' : type.key}
              onChange={handleTypeChange}
            />
          </div>
          <div className={styles.type}>
            <Select
              label="AO Type"
              options={aoTypeOptions}
              value={selectedAoType}
              onChange={handleAoTypeChange}
            />
          </div>
          <div className={styles.payments}>
            <Select
              options={latePayments.filter((item) => item.key).map((e) => ({ key: e.key, value: t(e.name) }))}
              value={!showLatePayments ? '' : showLatePayments.key}
              onChange={handleLatePaymentsChange}
              label={t('tables.expectedPaymentsTable.latePayments')}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpectedPaymentsHeader;
